import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useClerk } from "@clerk/clerk-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Palette,
  Users,
  Plus,
  LogOut as LogOutIcon,
  Loader2,
  Download,
  ShieldAlert,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  ThemeSwitcher,
  Label,
  Input,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  cn,
  toast,
  CircleImageWithSkeleton,
} from "@cliently/ui";
import {
  useOrganizations,
  useJoinOrganization,
  useLeaveOrganization,
} from "../hooks/use-organizations";
import { ALL_CLIENTS_KEY, useOrgStore } from "../stores/org.store";
import { api } from "../lib/api";
import { LottieIllustration } from "../components/LottieIllustration";
import emptyBoxLottie from "../assets/lottie/empty-box.json";

const RGPD_ANONYMIZE_CONFIRM = "DELETE_MY_DATA";



export const SettingsPage = () => {
  const { t } = useTranslation();

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">
          {t("settings.title")}
        </h1>
        <p className="text-muted-foreground">{t("settings.subtitle")}</p>
      </div>

      <Tabs defaultValue="appearance" className="space-y-6">
        <div className="-mx-4 w-full overflow-x-auto px-4 pb-1 md:mx-0 md:overflow-visible md:px-0">
          <TabsList className="inline-flex w-max">
            <TabsTrigger value="appearance" className="shrink-0 gap-1.5">
              <Palette className="h-4 w-4" />
              {t("settings.tabAppearance")}
            </TabsTrigger>
            <TabsTrigger value="practitioners" className="shrink-0 gap-1.5">
              <Users className="h-4 w-4" />
              {t("settings.tabPractitioners")}
            </TabsTrigger>
            <TabsTrigger value="privacy" className="shrink-0 gap-1.5">
              <ShieldAlert className="h-4 w-4" />
              {t("settings.tabPrivacy")}
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="appearance">
          <Card>
            <CardHeader>
              <div className="flex items-start gap-3">
                <Palette className="text-primary mt-0.5 h-5 w-5 shrink-0" />
                <div className="min-w-0 space-y-1">
                  <CardTitle>{t("settings.appearance")}</CardTitle>
                  <CardDescription>
                    {t("settings.appearanceHint")}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="flex flex-col gap-6">
              <div className="space-y-3">
                <Label className="font-medium">
                  {t("settings.colorTheme")}
                </Label>
                <div className="bg-muted/40 rounded-xl border p-4 sm:p-5">
                  <ThemeSwitcher variant="prominent" />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="practitioners">
          <MyPractitionersCard />
        </TabsContent>

        <TabsContent value="privacy">
          <DataPrivacyCard />
        </TabsContent>
      </Tabs>
    </div>
  );
}



const DataPrivacyCard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { signOut } = useClerk();
  const { data: organizations } = useOrganizations();
  const { activeClientId, switchOrg } = useOrgStore();
  const isAllProsMode = activeClientId === ALL_CLIENTS_KEY;

  const [downloadPending, setDownloadPending] = useState<boolean>(false);
  const [anonymizeTarget, setAnonymizeTarget] = useState<{
    orgId: string;
    orgName: string;
  } | null>(null);
  const [confirmText, setConfirmText] = useState<string>("");

  const anonymizeSingle = useMutation({
    mutationFn: async () => {
      await api.post("/portal/my-data/anonymize", {
        confirm: RGPD_ANONYMIZE_CONFIRM,
      });
    },
    onSuccess: () => {
      toast.success(
        t(
          "settings.rgpd.anonymizeSuccess",
          "Vos données personnelles ont été effacées pour ce praticien. Les factures peuvent être conservées pour obligation légale.",
        ),
      );
      setAnonymizeTarget(null);
      setConfirmText("");
      void queryClient.invalidateQueries();
      const orgs = organizations ?? [];
      if (orgs.length <= 1) {
        void signOut();
        navigate("/");
        return;
      }
      const removed = orgs.find((o) => o.clientId === activeClientId);
      if (removed) {
        const next = orgs.find((o) => o.orgId !== removed.orgId);
        if (next) {
          switchOrg(next.clientId);
          navigate("/dashboard");
        }
      }
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })
        ?.response?.data?.message;
      toast.error(
        msg ??
          t(
            "settings.rgpd.anonymizeError",
            "Impossible de finaliser la demande.",
          ),
      );
    },
  });

  const anonymizeAggregate = useMutation({
    mutationFn: async (orgId: string) => {
      await api.post("/portal/aggregate/my-data/anonymize", {
        orgId,
        confirm: RGPD_ANONYMIZE_CONFIRM,
      });
    },
    onSuccess: (_data, orgId) => {
      toast.success(
        t(
          "settings.rgpd.anonymizeSuccess",
          "Vos données personnelles ont été effacées pour ce praticien. Les factures peuvent être conservées pour obligation légale.",
        ),
      );
      setAnonymizeTarget(null);
      setConfirmText("");
      void queryClient.invalidateQueries();
      const orgs = organizations ?? [];
      const remaining = orgs.filter((o) => o.orgId !== orgId);
      if (remaining.length === 0) {
        void signOut();
        navigate("/");
        return;
      }
      const wasActive = orgs.find(
        (o) => o.orgId === orgId && o.clientId === activeClientId,
      );
      if (wasActive && remaining[0]) {
        switchOrg(remaining[0].clientId);
        navigate("/dashboard");
      }
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })
        ?.response?.data?.message;
      toast.error(
        msg ??
          t(
            "settings.rgpd.anonymizeError",
            "Impossible de finaliser la demande.",
          ),
      );
    },
  });

  const handleDownload = async () => {
    setDownloadPending(true);
    try {
      const url = isAllProsMode
        ? "/portal/aggregate/my-data/portable"
        : "/portal/my-data/portable";
      const res = await api.get(url, { responseType: "blob" });
      const blob = res.data as Blob;
      const filename = isAllProsMode
        ? "cliently-mes-donnees-tous-praticiens.json"
        : "cliently-mes-donnees.json";
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(href);
      toast.success(
        t("settings.rgpd.downloadSuccess", "Téléchargement lancé."),
      );
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 401 && isAllProsMode) {
        toast.error(
          t(
            "settings.rgpd.downloadAggregateAuthError",
            "Export multi-praticiens impossible : reconnectez-vous.",
          ),
        );
      } else {
        toast.error(
          t(
            "settings.rgpd.downloadError",
            "Impossible de télécharger l’export.",
          ),
        );
      }
    } finally {
      setDownloadPending(false);
    }
  };

  const confirmOk = confirmText.trim() === RGPD_ANONYMIZE_CONFIRM;
  const activeOrgName =
    organizations?.find((o) => o.clientId === activeClientId)?.orgName ??
    "";

  const openAnonymize = () => {
    if (isAllProsMode) return;
    const org = organizations?.find((o) => o.clientId === activeClientId);
    if (!org) return;
    setAnonymizeTarget({ orgId: org.orgId, orgName: org.orgName });
    setConfirmText("");
  };

  const submitAnonymize = () => {
    if (!confirmOk || !anonymizeTarget) return;
    if (isAllProsMode) {
      anonymizeAggregate.mutate(anonymizeTarget.orgId);
    } else {
      anonymizeSingle.mutate();
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <ShieldAlert className="text-primary h-5 w-5" />
            <CardTitle>
              {t("settings.rgpd.title", "Données personnelles (RGPD)")}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <p className="text-muted-foreground text-sm">
              {t(
                "settings.rgpd.portabilityHint",
                "Téléchargez une copie de vos données (rendez-vous, factures, objectifs, etc.) au format JSON, conformément au droit à la portabilité.",
              )}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void handleDownload()}
              disabled={downloadPending}
            >
              {downloadPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              {isAllProsMode
                ? t(
                    "settings.rgpd.downloadAll",
                    "Télécharger toutes mes données (tous praticiens)",
                  )
                : t("settings.rgpd.downloadOne", "Télécharger mes données")}
            </Button>
          </div>

          <div className="border-t pt-4">
            <p className="text-muted-foreground text-sm">
              {t(
                "settings.rgpd.eraseHint",
                "Demander l’effacement de vos données nominatives chez un praticien (pseudonymisation). Les factures peuvent être conservées pour la comptabilité.",
              )}
            </p>
            {isAllProsMode ? (
              <div className="mt-3 space-y-2">
                {(organizations ?? []).map((org) => (
                  <div
                    key={org.orgId}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3"
                  >
                    <span className="text-sm font-medium">{org.orgName}</span>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => {
                        setAnonymizeTarget({
                          orgId: org.orgId,
                          orgName: org.orgName,
                        });
                        setConfirmText("");
                      }}
                    >
                      {t("settings.rgpd.eraseForOrg", "Effacer mes données")}
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                className="mt-3"
                onClick={openAnonymize}
                disabled={!organizations?.length}
              >
                {t(
                  "settings.rgpd.eraseCurrent",
                  "Effacer mes données chez {{name}}",
                  { name: activeOrgName || "…" },
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog
        open={!!anonymizeTarget}
        onOpenChange={(open) => {
          if (!open) {
            setAnonymizeTarget(null);
            setConfirmText("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader className="space-y-4">
            <DialogTitle>
              {t("settings.rgpd.confirmTitle", "Confirmer l’effacement ?")}
            </DialogTitle>
            <DialogDescription className="space-y-2">
              <span className="block">
                {t(
                  "settings.rgpd.confirmDescription",
                  "Cette action est irréversible pour {{name}} : votre compte client sera dissocié, vos notes et documents personnels supprimés ou anonymisés.",
                  { name: anonymizeTarget?.orgName ?? "" },
                )}
              </span>
              <span className="text-foreground block text-sm font-medium">
                {t(
                  "settings.rgpd.typePhrase",
                  "Tapez {{phrase}} pour confirmer :",
                  { phrase: RGPD_ANONYMIZE_CONFIRM },
                )}
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="pt-4">
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={RGPD_ANONYMIZE_CONFIRM}
              className="font-mono"
              autoComplete="off"
            />
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setAnonymizeTarget(null);
                setConfirmText("");
              }}
            >
              {t("common.cancel", "Annuler")}
            </Button>
            <Button
              variant="destructive"
              disabled={
                !confirmOk ||
                anonymizeSingle.isPending ||
                anonymizeAggregate.isPending
              }
              onClick={submitAnonymize}
            >
              {(anonymizeSingle.isPending || anonymizeAggregate.isPending) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {t("settings.rgpd.confirmErase", "Effacer mes données")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}



const MyPractitionersCard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: organizations } = useOrganizations();
  const leaveOrg = useLeaveOrganization();
  const { activeClientId, switchOrg } = useOrgStore();

  const [leaveTarget, setLeaveTarget] = useState<{
    orgId: string;
    orgName: string;
  } | null>(null);
  const [joinOpen, setJoinOpen] = useState<boolean>(false);

  const handleLeave = () => {
    if (!leaveTarget) return;
    leaveOrg.mutate(leaveTarget.orgId, {
      onSuccess: () => {
        toast.success(
          t("settings.practitioners.leftSuccess", "Vous avez quitté {{name}}", {
            name: leaveTarget.orgName,
          }),
        );
        setLeaveTarget(null);
        if (organizations) {
          const remaining = organizations.filter(
            (o) => o.orgId !== leaveTarget.orgId,
          );
          if (
            activeClientId ===
            organizations.find((o) => o.orgId === leaveTarget.orgId)?.clientId
          ) {
            const next = remaining[0];
            if (next) {
              switchOrg(next.clientId);
              navigate("/dashboard");
            }
          }
        }
      },
      onError: (err: unknown) => {
        const msg = (err as { response?: { data?: { message?: string } } })
          ?.response?.data?.message;
        toast.error(
          msg ??
            t(
              "settings.practitioners.leftError",
              "Impossible de quitter ce praticien",
            ),
        );
      },
    });
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="text-primary h-5 w-5" />
              <CardTitle>
                {t("settings.practitioners.title", "Mes praticiens")}
              </CardTitle>
            </div>
            <Button size="sm" onClick={() => setJoinOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              {t("settings.practitioners.join", "Rejoindre")}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!organizations || organizations.length === 0 ? (
            <div className="text-muted-foreground flex flex-col items-center gap-2 py-2 text-center text-sm">
              <LottieIllustration
                animationData={emptyBoxLottie}
                size={150}
                className="opacity-90"
              />
              <p>
                {t(
                  "settings.practitioners.empty",
                  "Vous n'êtes lié à aucun praticien.",
                )}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {organizations.map((org) => (
                <div
                  key={org.clientId}
                  className={cn(
                    "flex items-center justify-between rounded-lg border p-3",
                    org.clientId === activeClientId &&
                      "border-primary/30 bg-primary/5",
                  )}
                >
                  <div className="flex items-center gap-3">
                    {org.orgLogo ? (
                      <CircleImageWithSkeleton
                        src={org.orgLogo}
                        alt=""
                        className="h-10 w-10"
                        fallback={
                          <div className="bg-primary/10 text-primary flex h-full w-full items-center justify-center rounded-full text-sm font-bold">
                            {org.orgName[0]}
                          </div>
                        }
                      />
                    ) : (
                      <div className="bg-primary/10 text-primary flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold">
                        {org.orgName[0]}
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium">{org.orgName}</p>
                      <p className="text-muted-foreground text-xs">
                        {t("settings.practitioners.since", "Depuis le")}{" "}
                        {new Date(org.joinedAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={organizations.length <= 1}
                    onClick={() =>
                      setLeaveTarget({ orgId: org.orgId, orgName: org.orgName })
                    }
                    className="text-destructive hover:text-destructive"
                  >
                    <LogOutIcon className="mr-1 h-4 w-4" />
                    {t("settings.practitioners.leave", "Quitter")}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={!!leaveTarget}
        onOpenChange={(open) => !open && setLeaveTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("settings.practitioners.leaveTitle", "Quitter ce praticien ?")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "settings.practitioners.leaveDescription",
                "Vous allez quitter {{name}}. Vos données (rendez-vous, notes, factures) resteront accessibles au praticien mais vous n'y aurez plus accès.",
                { name: leaveTarget?.orgName },
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLeaveTarget(null)}>
              {t("common.cancel", "Annuler")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleLeave}
              disabled={leaveOrg.isPending}
            >
              {leaveOrg.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {t("settings.practitioners.confirmLeave", "Quitter")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <JoinOrganizationDialog open={joinOpen} onOpenChange={setJoinOpen} />
    </>
  );
}



const JoinOrganizationDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useTranslation();
  const joinOrg = useJoinOrganization();

  const [accessCode, setAccessCode] = useState<string>("");

  const resetForm = () => setAccessCode("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    joinOrg.mutate(accessCode.trim(), {
      onSuccess: (data) => {
        toast.success(
          t(
            "settings.practitioners.joinedSuccess",
            "Vous avez rejoint {{name}}",
            {
              name: data.orgName,
            },
          ),
        );
        resetForm();
        onOpenChange(false);
      },
      onError: (err: unknown) => {
        const msg = (err as { response?: { data?: { message?: string } } })
          ?.response?.data?.message;
        toast.error(
          msg ??
            t(
              "settings.practitioners.joinedError",
              "Impossible de rejoindre ce praticien",
            ),
        );
      },
    });
  };

  const canSubmit = accessCode.trim().length > 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) resetForm();
        onOpenChange(v);
      }}
    >
      <DialogContent className="max-w-md gap-0 p-6 sm:rounded-2xl">
        <form onSubmit={handleSubmit} className="space-y-6">
          <DialogHeader className="space-y-1.5">
            <DialogTitle className="text-lg font-semibold">
              {t("settings.practitioners.joinTitle", "Rejoindre un praticien")}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">
              {t(
                "settings.practitioners.joinDescription",
                "Entrez le code d'association fourni par votre praticien.",
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="accessCode">
              {t("settings.practitioners.accessCode", "Code d'association")}
            </Label>
            <Input
              id="accessCode"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
              placeholder="Ex: A1B2C3D4E5F6"
              className="font-mono uppercase tracking-wider"
              autoFocus
            />
          </div>

          <DialogFooter className="border-t pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t("common.cancel", "Annuler")}
            </Button>
            <Button type="submit" disabled={!canSubmit || joinOrg.isPending}>
              {joinOrg.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {t("settings.practitioners.confirmJoin", "Rejoindre")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
